/**
 * Fuzzy search utility for command palette
 */

export interface SearchableItem {
  id: string;
  title: string;
  description?: string;
  keywords?: string[];
  category?: string;
  action?: () => void;
  shortcut?: string[];
  icon?: string;
  priority?: number;
}

export interface SearchResult extends SearchableItem {
  score: number;
  highlightedTitle: string;
  highlightedDescription?: string;
}

/**
 * Calculate fuzzy match score between query and text
 */
function calculateScore(query: string, text: string): number {
  const queryLower = query.toLowerCase().trim();
  const textLower = text.toLowerCase();

  if (!queryLower) return 0;
  if (textLower.includes(queryLower)) return 100;

  let score = 0;
  let queryIndex = 0;
  let consecutiveMatches = 0;

  for (let i = 0; i < textLower.length && queryIndex < queryLower.length; i++) {
    if (textLower[i] === queryLower[queryIndex]) {
      score += 1 + consecutiveMatches;
      consecutiveMatches++;
      queryIndex++;
    } else {
      consecutiveMatches = 0;
    }
  }

  // Bonus for matching all characters
  if (queryIndex === queryLower.length) {
    score += 10;
  }

  // Penalty for length difference
  score -= Math.abs(textLower.length - queryLower.length) * 0.1;

  return Math.max(0, score);
}

/**
 * Highlight matching characters in text
 */
function highlightText(text: string, query: string): string {
  if (!query.trim()) return text;

  const queryLower = query.toLowerCase();
  const textLower = text.toLowerCase();

  let result = "";
  let queryIndex = 0;

  for (let i = 0; i < text.length; i++) {
    if (
      queryIndex < queryLower.length &&
      textLower[i] === queryLower[queryIndex]
    ) {
      result += `<mark class="bg-yellow-200 dark:bg-yellow-800">${text[i]}</mark>`;
      queryIndex++;
    } else {
      result += text[i];
    }
  }

  return result;
}

/**
 * Perform fuzzy search on items
 */
export function fuzzySearch(
  items: SearchableItem[],
  query: string,
  options: {
    maxResults?: number;
    minScore?: number;
    searchFields?: ("title" | "description" | "keywords" | "category")[];
  } = {},
): SearchResult[] {
  const {
    maxResults = 50,
    minScore = 0.1,
    searchFields = ["title", "description", "keywords", "category"],
  } = options;

  if (!query.trim()) {
    return items
      .map((item) => ({
        ...item,
        score: item.priority || 0,
        highlightedTitle: item.title,
        highlightedDescription: item.description,
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, maxResults);
  }

  const results: SearchResult[] = [];

  for (const item of items) {
    let maxScore = 0;

    // Search in specified fields
    if (searchFields.includes("title")) {
      maxScore = Math.max(maxScore, calculateScore(query, item.title));
    }

    if (searchFields.includes("description") && item.description) {
      maxScore = Math.max(
        maxScore,
        calculateScore(query, item.description) * 0.8,
      );
    }

    if (searchFields.includes("keywords") && item.keywords) {
      for (const keyword of item.keywords) {
        maxScore = Math.max(maxScore, calculateScore(query, keyword) * 0.9);
      }
    }

    if (searchFields.includes("category") && item.category) {
      maxScore = Math.max(maxScore, calculateScore(query, item.category) * 0.7);
    }

    // Apply priority boost
    if (item.priority) {
      maxScore += item.priority * 0.1;
    }

    if (maxScore >= minScore) {
      results.push({
        ...item,
        score: maxScore,
        highlightedTitle: highlightText(item.title, query),
        highlightedDescription: item.description
          ? highlightText(item.description, query)
          : undefined,
      });
    }
  }

  return results.sort((a, b) => b.score - a.score).slice(0, maxResults);
}

/**
 * Group search results by category
 */
export function groupByCategory(
  results: SearchResult[],
): Record<string, SearchResult[]> {
  return results.reduce(
    (groups, result) => {
      const category = result.category || "General";
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(result);
      return groups;
    },
    {} as Record<string, SearchResult[]>,
  );
}
